import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Settings, LogOut, Table, ShieldCheck, Brain } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth';
import { useCompany } from '@/context/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useMobileMenu } from '@/context/MobileMenuContext';

export function CashFlowSidebar() {
  const location = useLocation();
  const navigate  = useNavigate();
  const { signOut, user }               = useAuth();
  const { t }                           = useTranslation();
  const { activeCompanyRole }           = useCompany();
  const { can, isPremium, isSuperAdmin } = usePermissions();
  const { isMobileMenuOpen, setIsMobileMenuOpen } = useMobileMenu();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try { await signOut(); navigate('/'); }
    catch (e) { console.error(e); }
    finally { setIsLoggingOut(false); }
  };

  const navItems = [
    { nameKey: 'nav.dashboard',        path: '/app/cashflow',          icon: <LayoutDashboard className="w-5 h-5" /> },
    { nameKey: 'nav.cashflowTables',   path: '/app/cashflow/tables',   icon: <Table           className="w-5 h-5" /> },
    ...(can({ module: 'cashflow', level: 'editor' })
      ? [{ nameKey: 'nav.cashflowSettings', path: '/app/cashflow/settings', icon: <Settings className="w-5 h-5" /> }]
      : []),
    { nameKey: 'nav.aiReports',         path: '/app/reports/ai',        icon: <Brain           className="w-5 h-5" /> },
    { nameKey: 'nav.settings',         path: '/app/settings',          icon: <Settings        className="w-5 h-5" /> },
    ...(isSuperAdmin
      ? [{ nameKey: 'nav.superAdmin',  path: '/app/admin',             icon: <ShieldCheck     className="w-5 h-5" /> }]
      : []),
  ];

  return (
    <>
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed md:static inset-y-0 right-0 z-40 md:z-30
          w-64 bg-white border-l md:border-l-0 md:border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-center h-16 border-b border-gray-100">
            <h1 className="text-2xl font-bold text-primary-600">Axelio</h1>
            <span className="ml-2 px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
              תזרים
            </span>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-150 text-sm
                  ${isActive(item.path)
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'}
                `}
              >
                {item.icon}
                <span>{t(item.nameKey)}</span>
              </Link>
            ))}
          </nav>

          <div className="p-4 border-t border-gray-200 space-y-3">
            {(isPremium || isSuperAdmin) && (
              <div className={`
                px-3 py-1.5 rounded-full text-xs font-semibold text-center
                ${isSuperAdmin ? 'bg-blue-50 text-blue-700' : 'bg-amber-100 text-amber-700'}
              `}>
                {isSuperAdmin ? 'Super Admin' : 'Premium'}
              </div>
            )}

            {!isSuperAdmin && activeCompanyRole === 'member' && (
              <div className="px-3 py-1.5 rounded-full text-xs font-semibold text-center bg-gray-100 text-gray-600">
                עובד
              </div>
            )}

            {user?.email && (
              <div className="px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Signed in as</p>
                <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
              </div>
            )}

            <button
              className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 disabled:opacity-50"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <LogOut className={`w-5 h-5 ${isLoggingOut ? 'animate-spin' : ''}`} />
              <span>{isLoggingOut ? t('common.loading') : t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
